/**
 * @module stores/tabs
 * @description 탭 워크스페이스 Pinia 스토어 — 멀티 탭 레이아웃의 탭 목록과
 * 활성 탭을 관리합니다. 최대 10개 탭 제한, 탭 고정(pin), 드래그 정렬(moveTab),
 * sessionStorage 영속화를 지원합니다.
 */
import { defineStore } from 'pinia'

const MAX_TABS = 10
const MAX_CACHE = 10
const STORAGE_KEY = 'workspace-tabs'

export { MAX_CACHE }

export const useTabStore = defineStore('tabs', {
  state: () => ({
    tabs: [],
    activeTabId: '',
    dirtyPaths: []
  }),
  getters: {
    /** @returns {object|undefined} 현재 활성화된 탭 객체 */
    activeTab: (state) => state.tabs.find((t) => t.id === state.activeTabId),
    /** @returns {string} 현재 활성 탭의 title (없으면 빈 문자열) */
    activeTabTitle: (state) => {
      const t = state.tabs.find((x) => x.id === state.activeTabId)
      return t ? t.title : ''
    },
    /** @returns {string} 현재 활성 탭의 fullPath (activeTabId와 동일) */
    activeTabPath: (state) => state.activeTabId || ''
  },
  actions: {
    /**
     * 라우트에 해당하는 탭을 열거나 이미 열려 있으면 활성화합니다.
     * menuYn이 false이거나 mobile 플랫폼 라우트는 탭으로 열지 않습니다.
     *
     * @param {object} route - Vue Router 라우트 객체
     * 제목은 메뉴 이름 → route.meta.title → route.name 순서이고, 이미 열린 탭도 메뉴 이름이 오면 그 이름으로 바꿉니다 (SURINP-688).
     *
     * @param {object} route - Vue Router 라우트 객체
     * @param {string} [menuName] - 탭 제목으로 사용할 메뉴 이름
     * @returns {boolean|undefined} 탭이 열리면 true, 무시되면 false
     */
    openTab(route, menuName) {
      if (route.meta.menuYn !== true || route.meta.platform !== 'web') {
        return false
      }

      const existing = this.tabs.find((t) => t.id === route.fullPath)
      if (existing) {
        this.activeTabId = route.fullPath
        if (menuName && existing.title !== menuName) {
          this.updateTabTitle(existing.id, menuName)
        }
        return true
      }

      if (this.tabs.length >= MAX_TABS) {
        window.appAlert('탭이 너무 많습니다. 일부 탭을 닫아주세요.')
        return false
      }

      const matched = route.matched?.[route.matched.length - 1]
      const componentName = matched?.components?.default?.name || ''
      const title = menuName || route.meta.title || (route.name || '').replace(/ 파라미터$/, '')
      const newTab = {
        id: route.fullPath,
        title,
        fullPath: route.fullPath,
        name: route.name,
        componentName,
        closable: true,
        pinned: false
      }
      this.tabs = [...this.tabs, newTab]
      this.activeTabId = newTab.id
      this._persistToStorage()
    },

    /**
     * 지정한 탭을 닫습니다.
     * 고정된 탭이거나 마지막 탭이면 닫지 않습니다.
     * 활성 탭이 닫히는 경우 인접 탭으로 전환합니다.
     *
     * @param {string} tabId - 닫을 탭의 ID (fullPath)
     * @returns {string|undefined} 활성 탭이 닫힌 경우 이동할 탭의 fullPath
     */
    closeTab(tabId) {
      const tab = this.tabs.find((t) => t.id === tabId)
      if (!tab || tab.pinned) return

      if (this.tabs.length <= 1) {
        return
      }

      const index = this.tabs.findIndex((t) => t.id === tabId)
      if (index === -1) return

      this.tabs = this.tabs.filter((t) => t.id !== tabId)

      if (tabId === this.activeTabId) {
        const nextTab = this.tabs[index - 1] || this.tabs[index]
        this.activeTabId = nextTab.id
        this._persistToStorage()
        return nextTab.fullPath
      }
      this._persistToStorage()
    },

    /**
     * 지정한 탭을 활성 탭으로 설정합니다.
     *
     * @param {string} tabId - 활성화할 탭의 ID (fullPath)
     */
    setActive(tabId) {
      this.activeTabId = tabId
      this._persistToStorage()
    },

    /**
     * 현재 라우트와 탭 목록을 동기화합니다.
     * router.afterEach 훅에서 호출됩니다.
     *
     * @param {object} route - Vue Router 라우트 객체
     * @param {string} [menuName] - 메뉴 마스터 이름 (없으면 라우트 제목)
     */
    syncWithRoute(route, menuName) {
      this.openTab(route, menuName)
    },

    /**
     * 열린 탭 제목을 메뉴 이름으로 맞춥니다. 메뉴 트리가 탭보다 늦게 로드될 때 menu 스토어가 호출합니다 (SURINP-688).
     * resolveName 이 빈 문자열을 주는 탭(상세·메뉴에 없는 화면)은 그대로 둡니다.
     *
     * @param {(fullPath: string) => string} resolveName - fullPath → 메뉴 이름
     */
    applyMenuTitles(resolveName) {
      let changed = false
      this.tabs = this.tabs.map((t) => {
        const name = resolveName(t.fullPath)
        if (!name || name === t.title) return t
        changed = true
        return { ...t, title: name }
      })
      if (changed) this._persistToStorage()
    },

    /**
     * 드래그 앤 드롭으로 탭 순서를 변경합니다.
     *
     * @param {number} fromIndex - 이동할 탭의 원래 인덱스
     * @param {number} toIndex - 이동할 목적지 인덱스
     */
    moveTab(fromIndex, toIndex) {
      const updated = [...this.tabs]
      const [moved] = updated.splice(fromIndex, 1)
      updated.splice(toIndex, 0, moved)
      this.tabs = updated
      this._persistToStorage()
    },

    /**
     * 탭 제목을 업데이트합니다.
     *
     * @param {string} tabId - 제목을 변경할 탭의 ID
     * @param {string} title - 새 탭 제목
     */
    updateTabTitle(tabId, title) {
      this.tabs = this.tabs.map((t) =>
        t.id === tabId ? { ...t, title } : t
      )
      this._persistToStorage()
    },

    /**
     * 탭을 고정합니다. 고정된 탭은 닫을 수 없으며 탭 목록 앞으로 이동합니다.
     *
     * @param {string} tabId - 고정할 탭의 ID
     */
    pinTab(tabId) {
      const pinnedCount = this.tabs.filter((t) => t.pinned).length
      this.tabs = this.tabs.map((t) =>
        t.id === tabId ? { ...t, pinned: true, closable: false } : t
      )
      const idx = this.tabs.findIndex((t) => t.id === tabId)
      if (idx > pinnedCount) {
        const updated = [...this.tabs]
        const [moved] = updated.splice(idx, 1)
        updated.splice(pinnedCount, 0, moved)
        this.tabs = updated
      }
      this._persistToStorage()
    },

    /**
     * 탭 고정을 해제합니다.
     *
     * @param {string} tabId - 고정 해제할 탭의 ID
     */
    unpinTab(tabId) {
      this.tabs = this.tabs.map((t) =>
        t.id === tabId ? { ...t, pinned: false, closable: true } : t
      )
      this._persistToStorage()
    },

    /**
     * 지정한 탭과 고정 탭을 제외한 모든 탭을 닫습니다.
     *
     * @param {string} tabId - 유지할 탭의 ID
     */
    closeOtherTabs(tabId) {
      this.tabs = this.tabs.filter((t) => t.id === tabId || t.pinned)
      if (!this.tabs.find((t) => t.id === this.activeTabId)) {
        this.activeTabId = tabId
      }
      this._persistToStorage()
    },

    /**
     * 지정한 탭의 오른쪽에 있는 고정되지 않은 탭을 모두 닫습니다.
     *
     * @param {string} tabId - 기준 탭의 ID
     */
    closeRightTabs(tabId) {
      const idx = this.tabs.findIndex((t) => t.id === tabId)
      if (idx === -1) return
      this.tabs = this.tabs.filter((t, i) => i <= idx || t.pinned)
      if (!this.tabs.find((t) => t.id === this.activeTabId)) {
        this.activeTabId = tabId
      }
      this._persistToStorage()
    },

    /**
     * 지정한 탭의 왼쪽에 있는 고정되지 않은 탭을 모두 닫습니다.
     *
     * @param {string} tabId - 기준 탭의 ID
     */
    closeLeftTabs(tabId) {
      const idx = this.tabs.findIndex((t) => t.id === tabId)
      if (idx === -1) return
      this.tabs = this.tabs.filter((t, i) => i >= idx || t.pinned)
      if (!this.tabs.find((t) => t.id === this.activeTabId)) {
        this.activeTabId = tabId
      }
      this._persistToStorage()
    },

    /**
     * sessionStorage에서 탭 상태를 복원합니다.
     * 앱 초기화 시 호출하여 페이지 새로고침 후에도 탭을 유지합니다.
     */
    restoreFromStorage() {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY)
        if (saved) {
          const { tabs, activeTabId } = JSON.parse(saved)
          if (Array.isArray(tabs) && tabs.length > 0) {
            this.tabs = tabs
            this.activeTabId = activeTabId || tabs[0].id
          }
        }
      } catch (e) {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    },

    /**
     * 현재 탭 상태를 sessionStorage에 직렬화하여 저장합니다.
     * sessionStorage 용량 초과 시 조용히 실패합니다.
     */
    _persistToStorage() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          tabs: this.tabs,
          activeTabId: this.activeTabId
        }))
      } catch (e) {
        // sessionStorage full — silent fail
      }
    },

    /**
     * 모든 탭을 초기화하고 sessionStorage에서 탭 데이터를 삭제합니다.
     * 로그아웃 시 호출합니다.
     */
    clearAll() {
      this.tabs = []
      this.activeTabId = ''
      this.dirtyPaths = []
      sessionStorage.removeItem(STORAGE_KEY)
    },

    /**
     * 특정 경로를 dirty로 표시합니다.
     * mutation(POST/PATCH/DELETE) 성공 후 호출하여,
     * 해당 목록 페이지가 activated될 때 데이터를 재조회하도록 합니다.
     *
     * @param {string} fullPath - dirty로 표시할 라우트 경로
     */
    markDirty(fullPath) {
      if (!this.dirtyPaths.includes(fullPath)) {
        this.dirtyPaths = [...this.dirtyPaths, fullPath]
      }
    },

    /**
     * 특정 경로가 dirty 상태인지 확인합니다.
     *
     * @param {string} fullPath - 확인할 라우트 경로
     * @returns {boolean}
     */
    isDirty(fullPath) {
      return this.dirtyPaths.includes(fullPath)
    },

    /**
     * 특정 경로의 dirty 상태를 해제합니다.
     * activated 훅에서 재조회 완료 후 호출합니다.
     *
     * @param {string} fullPath - 해제할 라우트 경로
     */
    clearDirty(fullPath) {
      this.dirtyPaths = this.dirtyPaths.filter(p => p !== fullPath)
    }
  }
})
